import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { adminService } from '../../services/api';
import { Assureur, PaginatedResponse } from '../../types';
import DataTable from '../common/DataTable';
import LoadingSpinner from '../common/LoadingSpinner';
import AssureurForm from './AssureurForm';

interface AssureurManagementProps {
  isVisible: boolean;
}

const AssureurManagement: React.FC<AssureurManagementProps> = ({ isVisible }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingAssureur, setEditingAssureur] = useState<Assureur | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const queryClient = useQueryClient();

  const { data: assureursData, isLoading } = useQuery<PaginatedResponse<Assureur>>({
    queryKey: ['admin-assureurs', searchTerm],
    keepPreviousData: true,
    queryFn: () => adminService.getAssureurs({ search: searchTerm }).then(res => res.data),
    enabled: isVisible,
  });

  const createAssureurMutation = useMutation({
    mutationFn: (data: Omit<Assureur, 'id' | 'created_at'>) => adminService.createAssureur(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-assureurs'] });
      setShowForm(false);
      setEditingAssureur(null);
    },
  });

  const updateAssureurMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Omit<Assureur, 'id' | 'created_at'>> }) =>
      adminService.updateAssureur(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-assureurs'] });
      setShowForm(false);
      setEditingAssureur(null);
    },
  });

  const deleteAssureurMutation = useMutation({
    mutationFn: (id: number) => adminService.deleteAssureur(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-assureurs'] });
    },
  });

  const handleSubmit = (data: Omit<Assureur, 'id' | 'created_at'>) => {
    if (editingAssureur) {
      updateAssureurMutation.mutate({ id: editingAssureur.id, data });
    } else {
      createAssureurMutation.mutate(data);
    }
  };

  const handleEdit = (assureur: Assureur) => {
    setEditingAssureur(assureur);
    setShowForm(true);
  };

  const handleDelete = (assureur: Assureur) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer l'assureur "${assureur.nom}" ?`)) {
      deleteAssureurMutation.mutate(assureur.id);
    }
  };

  const columns = [
    {
      key: 'nom',
      label: 'Nom',
    },
    {
      key: 'Contact',
      label: 'contact',
      render: (_: any, assureur: Assureur) => assureur.contact || '-',
    },
    {
      key: 'Téléphone',
      label: 'telephone',
      render: (_: any, assureur: Assureur) => assureur.telephone || '-',
    },
    {
      key: 'Email',
      label: 'email',
      render: (_: any, assureur: Assureur) => assureur.email || '-',
    },
    {
      key: 'Adresse',
      label: 'adresse',
      render: (_: any, assureur: Assureur) => assureur.adresse || '-',
    },
    {
      key: 'Taux de couverture',
      label: 'taux_couverture',
      render: (_: any, assureur: Assureur) => `${assureur.taux_couverture}%`,
    },
    {
      key: 'Date de création',
      label: 'created_at',
      render: (_: any, assureur: Assureur) => new Date(assureur.created_at).toLocaleDateString('fr-FR'),
    },
    {
      key: 'Actions',
      label: 'actions',
      sortable: false,
      render: (_: any, assureur: Assureur) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleEdit(assureur)}
            className="text-blue-600 hover:text-blue-900"
            title="Modifier"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleDelete(assureur)}
            className="text-red-600 hover:text-red-900"
            title="Supprimer"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  if (!isVisible) return null;

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Gestion des assureurs</h2>
          <p className="mt-1 text-sm text-gray-500">
            Gérez les compagnies d'assurance partenaires
          </p>
        </div>
        <button
          onClick={() => {
            setEditingAssureur(null);
            setShowForm(true);
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Nouvel assureur
        </button>
      </div>

      <div className="flex items-center space-x-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Rechercher un assureur..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
          />
        </div>
      </div>

      <DataTable
        data={assureursData?.results || []}
        columns={columns}
        loading={isLoading}
      />

      {showForm && (
        <AssureurForm
          assureur={editingAssureur}
          onSubmit={handleSubmit}
          onCancel={() => {
            setShowForm(false);
            setEditingAssureur(null);
          }}
          loading={createAssureurMutation.isPending || updateAssureurMutation.isPending}
        />
      )}
    </div>
  );
};

export default AssureurManagement;